import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice, fmtPct, changeColor, signalClass } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, YAxis } from "recharts";
import { Star, StarOff, ArrowUpRight, Sparkles } from "lucide-react";

function Sparkline({ data, positive }) {
  const points = data.map((v, i) => ({ i, v }));
  const color = positive ? "hsl(var(--signal-buy))" : "hsl(var(--signal-wait))";
  return (
    <ResponsiveContainer width="100%" height={40}>
      <AreaChart data={points} margin={{top:0,right:0,left:0,bottom:0}}>
        <defs>
          <linearGradient id={`sp-${positive}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={["auto","auto"]} />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#sp-${positive})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [watchQuotes, setWatchQuotes] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [q, w, n] = await Promise.all([
        api.get("/commodities/quotes"),
        api.get("/watchlist"),
        api.get("/analyst-notes"),
      ]);
      setQuotes(q.data);
      setWatchQuotes(w.data.quotes || []);
      setNotes(n.data.slice(0, 4));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); const id = setInterval(loadAll, 20000); return () => clearInterval(id); }, []);

  const toggleWatch = async (code) => {
    const inList = (user.watchlist || []).includes(code);
    const next = inList ? user.watchlist.filter(c => c !== code) : [...(user.watchlist || []), code];
    await api.put("/watchlist", { codes: next });
    toast.success(inList ? `Removed ${code} from watchlist` : `Added ${code} to watchlist`);
    await refresh(); await loadAll();
  };

  const movers = [...quotes].sort((a,b) => Math.abs(b.change_pct) - Math.abs(a.change_pct)).slice(0,3);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Terminal · {user?.persona}</div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.tier === "free" ? "Free tier — delayed data. Upgrade for real-time, AI signals & vendors." : `Active plan: ${user?.tier?.toUpperCase()}`}
          </p>
        </div>
      </div>

      {/* movers */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Top movers · 24h</div>
          <Link to="/app/markets" className="text-xs text-primary hover:underline">All markets →</Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {movers.map(q => (
            <Link
              key={q.code}
              to={`/commodity/${q.code}`}
              data-testid={`mover-${q.code.toLowerCase()}`}
              className="group rounded-lg border border-border/60 bg-card/60 p-4 transition hover:border-primary/40 hover:bg-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.category}</div>
                  <div className="font-display text-lg font-semibold">{q.name}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground transition group-hover:text-primary" />
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="num text-2xl font-semibold">{fmtPrice(q.price)}</div>
                  <div className={`text-xs font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)} · {q.unit}</div>
                </div>
                <div className="h-10 w-28"><Sparkline data={q.sparkline || []} positive={q.change_pct >= 0} /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        {/* watchlist */}
        <Card className="border-border/60 bg-card/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Your watchlist</CardTitle>
            <Link to="/app/markets" className="text-xs text-primary hover:underline">Manage →</Link>
          </CardHeader>
          <CardContent>
            {watchQuotes.length === 0 ? (
              <div className="rounded-md border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
                No commodities on your watchlist yet.<br/>
                <Link to="/app/markets" className="mt-2 inline-block text-primary hover:underline">Browse markets →</Link>
              </div>
            ) : (
              <div className="divide-y divide-border/60">
                {watchQuotes.map(q => (
                  <div key={q.code} data-testid={`watch-row-${q.code.toLowerCase()}`} className="flex items-center justify-between py-3">
                    <div>
                      <Link to={`/commodity/${q.code}`} className="text-sm font-semibold hover:text-primary">{q.name}</Link>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.code} · {q.unit}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="num text-sm font-semibold">{fmtPrice(q.price)}</div>
                        <div className={`text-xs font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)}</div>
                      </div>
                      <Button size="sm" variant="ghost" data-testid={`unwatch-${q.code.toLowerCase()}`} onClick={() => toggleWatch(q.code)}>
                        <Star className="h-4 w-4 fill-primary text-primary" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* analyst notes preview */}
        <Card className="border-border/60 bg-card/60">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Analyst feed</CardTitle>
            <Link to="/app/notes" className="text-xs text-primary hover:underline">All notes →</Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {notes.map(n => (
              <div key={n.id} data-testid={`note-preview-${n.id}`} className="rounded-md border border-border/60 bg-background/50 p-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="uppercase text-[10px]">{n.commodity_code}</Badge>
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${n.sentiment==="bullish"?"text-[hsl(var(--signal-buy))]":n.sentiment==="bearish"?"text-[hsl(var(--signal-wait))]":"text-[hsl(var(--signal-hold))]"}`}>
                    {n.sentiment}
                  </span>
                </div>
                <div className="mt-2 font-display text-sm font-semibold">{n.title}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2">{n.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* AI hint */}
      {user?.tier === "free" && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/15 text-primary"><Sparkles className="h-4 w-4" /></div>
              <div>
                <div className="font-display text-base font-semibold">Unlock AI Buy / Hold / Wait signals</div>
                <p className="text-sm text-muted-foreground">Powered by Claude Sonnet 5. Real-time data, vendor benchmarking and unlimited alerts.</p>
              </div>
            </div>
            <Link to="/app/pricing"><Button data-testid="dashboard-upgrade-cta" className="rounded-full">See plans</Button></Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
